import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";

const CourseMockTest = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { testId } = location.state || {};
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [optionFeedback, setOptionFeedback] = useState({}); // Track feedback for each option
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [score, setScore] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isTimerPlaying, setIsTimerPlaying] = useState(true);
    const [testResult, setTestResult] = useState(null);
    const [userData, setUserData] = useState(null);
    const [showDetailedResults, setShowDetailedResults] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false); // Track if explanation is shown
    const { bearerAuth } = useApi();
    const { getToken, user } = useAuth();

    const resolveMediaUrl = (path) => {
        if (!path) return null;

        const normalizePath = (value) =>
            String(value)
                .replace(/\\/g, "/")
                .replace(/\/public\/public\//g, "/public/")
                .replace(/\/backend\/public\/uploads\//g, "/backend/uploads/")
                .replace(/\/public\/uploads\//g, "/uploads/")
                .replace(/\/public\/storage\//g, "/storage/")
                .replace(/^backend\/public\/uploads\//, "backend/uploads/")
                .replace(/^public\/public\//, "public/")
                .replace(/^public\/uploads\//, "uploads/")
                .replace(/^public\/storage\//, "storage/")
                .replace(/^\/+/, "");

        if (/^https?:\/\//i.test(path)) {
            return normalizePath(path);
        }

        const clean = normalizePath(path);
        const base = (process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
        if (clean.startsWith("uploads/")) return `${base}/${clean}`;
        if (clean.startsWith("storage/")) return `${base}/${clean}`;
        if (clean.startsWith("backend/uploads/")) return `${base}/${clean}`;
        return base ? `${base}/${clean}` : `/${clean}`;
    };

    useEffect(() => {
        const fetchUserData = async () => {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                setUserData(parsedUser);
            }
        };

        fetchUserData();

        if (!testId) {
            navigate('/mock-test');
            return;
        }

        const fetchQuestions = async () => {
            const token = getToken();
            try {
                const response = await bearerAuth(
                    `mock-tests/questions/list`,
                    "POST",
                    { test_id: testId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.data.status) {
                    // Add full URLs for images and videos
                    const questionsWithMediaUrls = response.data.data.map(question => {
                        const updatedQuestion = { ...question };
                        
                        // Add image URL if image exists
                        const imagePath = question.image_url || question.image || null;
                        if (imagePath) {
                            updatedQuestion.fullImageUrl = resolveMediaUrl(imagePath);
                        }
                        
                        // Add video URL if video exists
                        const videoPath = question.video_url || question.video || null;
                        if (videoPath) {
                            updatedQuestion.fullVideoUrl = resolveMediaUrl(videoPath);
                        }
                        
                        return updatedQuestion;
                    });
                    setQuestions(questionsWithMediaUrls);
                } else {
                    setError('No questions found for this test.');
                }
            } catch (err) {
                console.error('Error fetching questions:', err);
                setError('Failed to load questions. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuestions();
    }, [testId, navigate]);

    const handleOptionSelect = (questionId, option) => {
        const currentQuestion = questions.find(q => q.id === questionId);
        const isCorrect = option === currentQuestion.correct_option;
        
        // Update selected option
        setSelectedOptions(prev => ({
            ...prev,
            [questionId]: option
        }));

        // Set feedback for this option
        setOptionFeedback(prev => ({
            ...prev,
            [questionId]: {
                selectedOption: option,
                isCorrect: isCorrect,
                explanation: currentQuestion.explanation
            }
        }));

        // Show explanation
        setShowExplanation(true);
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            setShowExplanation(false); // Hide explanation for next question
        }
    };

    const handlePrevQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
            // Check if previous question has a selected option to show explanation
            const prevQuestion = questions[currentQuestionIndex - 1];
            if (prevQuestion && selectedOptions[prevQuestion.id]) {
                setShowExplanation(true);
            } else {
                setShowExplanation(false);
            }
        }
    };

    const handleSubmitTest = async () => {
        setIsTimerPlaying(false);

        try {
            const answers = {};
            questions.forEach(question => {
                if (selectedOptions[question.id]) {
                    answers[question.id] = selectedOptions[question.id];
                }
            });

            const user = JSON.parse(localStorage.getItem('user'));
            const token = getToken();
            
            const response = await bearerAuth(
                `mock-tests/submit`,
                "POST",
                {
                    id: user.id, 
                    test_id: testId,
                    answers: answers,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.status) {
                const result = response.data.data;
                setTestResult(result);

                let correctCount = 0;
                questions.forEach(question => {
                    if (answers[question.id] &&
                        answers[question.id] === result.correctAnswers[question.id]) {
                        correctCount++;
                    }
                });

                const calculatedScore = Math.round((correctCount / questions.length) * 100);
                setScore(calculatedScore);
                setIsSubmitted(true);
            } else {
                throw new Error(response.data.message || 'Failed to submit test');
            }
        } catch (error) {
            console.error('Error submitting test:', error);
            let correctCount = 0;
            questions.forEach(question => {
                if (selectedOptions[question.id] === question.correct_option) {
                    correctCount++;
                }
            });
            const calculatedScore = Math.round((correctCount / questions.length) * 100);
            setScore(calculatedScore);
            setIsSubmitted(true);
            alert('Test submitted but could not save results. Your score has been calculated locally.');
        }
    };

    const getOptionClassName = (questionId, option) => {
        const feedback = optionFeedback[questionId];
        if (!feedback || feedback.selectedOption !== option) {
            return "list-group-item list-group-item-action";
        }
        
        if (feedback.isCorrect) {
            return "list-group-item list-group-item-action list-group-item-success";
        } else {
            return "list-group-item list-group-item-action list-group-item-danger";
        }
    };

    const renderQuestionResult = (question, index) => {
        const userAnswer = selectedOptions[question.id] || 'Not answered';
        const correctAnswer = testResult?.correctAnswers[question.id] || question.correct_option;
        const isCorrect = userAnswer === correctAnswer;

        return (
            <div key={question.id} className="mb-4 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Question {index + 1}</h5>
                    <span className={`badge ${isCorrect ? 'bg-success' : 'bg-danger'}`}>
                        {isCorrect ? 'Correct' : 'Incorrect'}
                    </span>
                </div>

                <p className="fw-bold mb-3">{question.question}</p>

                {/* Display image in results */}
                {question.image && question.fullImageUrl && (
                    <div className="mb-3 text-center">
                        <img
                            src={question.fullImageUrl}
                            alt="Question illustration"
                            className="img-fluid rounded"
                            style={{ maxHeight: '400px' }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                )}

                {/* Keep detailed results compact: open video in a new tab instead of large embed */}
                {question.video && question.fullVideoUrl && (
                    <div className="mb-3">
                        <a
                            href={question.fullVideoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                        >
                            <i className="fas fa-play-circle me-1"></i>
                            View Video
                        </a>
                    </div>
                )}

                <div className="row">
                    <div className="col-md-6">
                        <div className="card mb-3">
                            <div className="card-header bg-info text-white">
                                Your Answer
                            </div>
                            <div className="card-body">
                                {userAnswer === 'Not answered' ? (
                                    <span className="text-muted">Not answered</span>
                                ) : (
                                    <>
                                        <p className="mb-1">
                                            <strong>{userAnswer}:</strong> {question[`option_${userAnswer.toLowerCase()}`]}
                                        </p>
                                        {!isCorrect && (
                                            <span className="text-danger">
                                                <i className="fas fa-times-circle me-1"></i>
                                                Incorrect
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="card">
                            <div className="card-header bg-success text-white">
                                Correct Answer
                            </div>
                            <div className="card-body">
                                <p className="mb-1">
                                    <strong>{correctAnswer}:</strong> {question[`option_${correctAnswer.toLowerCase()}`]}
                                </p>
                                {isCorrect ? (
                                    <span className="text-success">
                                        <i className="fas fa-check-circle me-1"></i>
                                        You got it right!
                                    </span>
                                ) : (
                                    <span className="text-success">
                                        <i className="fas fa-check-circle me-1"></i>
                                        This was the correct answer
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Show explanation in results */}
                {question.explanation && (
                    <div className="mt-3 p-3 bg-light rounded">
                        <h6 className="mb-2">
                            <i className="fas fa-info-circle me-2 text-primary"></i>
                            Explanation:
                        </h6>
                        <p className="mb-0">{question.explanation}</p>
                    </div>
                )}

                {!isCorrect && userAnswer !== 'Not answered' && (
                    <div className="mt-3 p-2 bg-light rounded">
                        <p className="mb-0 text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            You selected {userAnswer} but the correct answer was {correctAnswer}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderQuestion = () => {
        if (isLoading) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading questions...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="alert alert-danger text-center">
                    {error}
                    <button className="btn btn-secondary mt-3" onClick={() => navigate('/mock-test')}>
                        Go Back
                    </button>
                </div>
            );
        }

        if (isSubmitted) {
            const correctCount = testResult ?
                Object.keys(testResult.correctAnswers).filter(key =>
                    selectedOptions[Number(key)] === testResult.correctAnswers[key]
                ).length :
                questions.filter(q => selectedOptions[q.id] === q.correct_option).length;

            return (
                <div className="test-results">
                    <div className="text-center py-4">
                        <h3>Test Results</h3>
                        <div className={`display-4 fw-bold ${score >= 70 ? 'text-success' : 'text-danger'}`}>
                            {score}%
                        </div>
                        <p className="mt-3">
                            {score >= 70 ? 'Congratulations! You passed!' : 'Sorry, you did not pass. Please try again.'}
                        </p>
                        <div className="progress mt-3" style={{ height: '20px' }}>
                            <div
                                className={`progress-bar ${score >= 70 ? 'bg-success' : 'bg-danger'}`}
                                role="progressbar"
                                style={{ width: `${score}%` }}
                                aria-valuenow={score}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            >
                                {score}%
                            </div>
                        </div>
                        <p className="mt-2">
                            You answered <strong>{correctCount}</strong> out of <strong>{questions.length}</strong> questions correctly.
                        </p>

                        <button
                            className={`btn ${showDetailedResults ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                            onClick={() => setShowDetailedResults(!showDetailedResults)}
                        >
                            {showDetailedResults ? 'Hide Detailed Results' : 'View Detailed Results'}
                            <i className={`fas ${showDetailedResults ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`}></i>
                        </button>

                        <button
                            className="btn btn-primary ms-3"
                            onClick={() => navigate('/dashboard/progress')}
                        >
                            Back to Progress
                        </button>
                    </div>

                    {showDetailedResults && (
                        <div className="detailed-results mt-4">
                            <h4 className="mb-4 text-center">Question-by-Question Breakdown</h4>
                            {questions.map((question, index) => renderQuestionResult(question, index))}

                            <div className="text-center mt-4">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => navigate('/dashboard/progress')}
                                >
                                    Back to Progress
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (questions.length === 0) {
            return (
                <div className="text-center py-5">
                    <p>No questions available for this test.</p>
                    <button className="btn btn-secondary" onClick={() => navigate('/mock-test')}>
                        Go Back
                    </button>
                </div>
            );
        }

        const currentQuestion = questions[currentQuestionIndex];
        const selectedOption = selectedOptions[currentQuestion.id];
        const currentFeedback = optionFeedback[currentQuestion.id];
        const hasMedia = (
            (currentQuestion.image && currentQuestion.fullImageUrl) ||
            (currentQuestion.video && currentQuestion.fullVideoUrl)
        );

        return (
            <div className="card shadow-sm">
                <div className="card-body">
                    <h4 className="card-title mb-4">{currentQuestion.question}</h4>
                    {currentQuestion.hint && (
                        <div className="alert alert-info py-2">
                            <strong>Hint:</strong> {currentQuestion.hint}
                        </div>
                    )}

                    <div className="row g-4 align-items-start">
                        {/* Display question image if it exists */}
                        {currentQuestion.image && currentQuestion.fullImageUrl && (
                            <div className="mb-4 text-center col-12 col-lg-5">
                                <img
                                    src={currentQuestion.fullImageUrl}
                                    alt="Question illustration"
                                    className="img-fluid rounded"
                                    style={{ width: '100%', maxHeight: '320px', objectFit: 'contain' }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}

                        {/* Display question video if it exists */}
                        {currentQuestion.video && currentQuestion.fullVideoUrl && (
                            <div className="mb-4 text-center col-12 col-lg-5">
                                <video 
                                    controls 
                                    key={currentQuestion?.key} 
                                    className="img-fluid rounded shadow-sm"
                                    style={{ width: '100%', maxHeight: '320px', backgroundColor: '#000' }}
                                >
                                    <source src={currentQuestion.fullVideoUrl} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        )}

                        <div className={hasMedia ? "list-group col-12 col-lg-7" : "list-group col-12"}>
                            {['A', 'B', 'C', 'D'].map(option => (
                            <button
                                key={option}
                                className={getOptionClassName(currentQuestion.id, option)}
                                onClick={() => handleOptionSelect(currentQuestion.id, option)}
                                disabled={selectedOption !== undefined} // Disable after selection
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>
                                        <strong>{option}:</strong> {currentQuestion[`option_${option.toLowerCase()}`]}
                                    </span>
                                    {selectedOption === option && (
                                        <span className={`badge ${currentFeedback?.isCorrect ? 'bg-success' : 'bg-danger'}`}>
                                            {currentFeedback?.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                                        </span>
                                    )}
                                </div>
                            </button>
                            ))}
                        </div>
                    </div>

                    {/* Show explanation after selection */}
                    {showExplanation && currentFeedback && (
                        <div className={`mt-4 p-3 rounded ${currentFeedback.isCorrect ? 'bg-success-subtle' : 'bg-danger-subtle'}`}>
                            <div className="d-flex align-items-start">
                                <i className={`fas fa-${currentFeedback.isCorrect ? 'check-circle' : 'times-circle'} fs-4 me-3 ${currentFeedback.isCorrect ? 'text-success' : 'text-danger'}`}></i>
                                <div>
                                    <h6 className={`mb-2 ${currentFeedback.isCorrect ? 'text-success' : 'text-danger'}`}>
                                        {currentFeedback.isCorrect ? 'Correct!' : 'Incorrect!'}
                                    </h6>
                                    {currentFeedback.explanation && (
                                        <>
                                            <p className="mb-1 fw-bold">Explanation:</p>
                                            <p className="mb-0">{currentFeedback.explanation}</p>
                                        </>
                                    )}
                                    {!currentFeedback.explanation && (
                                        <p className="mb-0">
                                            {currentFeedback.isCorrect 
                                                ? 'Great job! You selected the correct answer.' 
                                                : `The correct answer is ${currentQuestion.correct_option}.`}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="card-footer bg-light">
                    <div className="d-flex justify-content-between">
                        <button
                            className="btn btn-secondary"
                            onClick={handlePrevQuestion}
                            disabled={currentQuestionIndex === 0}
                        >
                            Previous
                        </button>
                        <div>
                            {selectedOption && (
                                <span className="badge bg-success me-2">
                                    <i className="fas fa-check me-1"></i>
                                    Answered
                                </span>
                            )}
                        </div>
                        {currentQuestionIndex < questions.length - 1 ? (
                            <button className="btn btn-primary" onClick={handleNextQuestion}>
                                Next
                            </button>
                        ) : (
                            <button className="btn btn-success" onClick={handleSubmitTest}>
                                Submit Test
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-lg-10 col-xl-9">
                    <div className="test-header mb-4 p-3 bg-light rounded shadow-sm">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <h4 className="mb-1 text-primary">
                                    <i className="fas fa-book me-2"></i>
                                    {userData?.CourseName || 'Course Test'}
                                </h4>
                                <p className="mb-0 text-muted">
                                    <i className="fas fa-user me-2"></i>
                                    {userData?.username || 'User'}
                                </p>
                            </div>
                            <div className="col-md-6 text-md-end mt-2 mt-md-0">
                                {!isSubmitted && questions.length > 0 && (
                                    <div className="d-flex align-items-center justify-content-end">
                                        <div className="me-3">
                                            <span className="badge bg-secondary">
                                                Q{currentQuestionIndex + 1}/{questions.length}
                                            </span>
                                        </div>
                                        <div className="timer-container">
                                            <CountdownCircleTimer
                                                isPlaying={isTimerPlaying}
                                                duration={1800}
                                                colors={['#0d6efd', '#ffc107', '#dc3545']}
                                                colorsTime={[1200, 600, 0]}
                                                size={70}
                                                strokeWidth={6}
                                                onComplete={() => {
                                                    handleSubmitTest();
                                                    return { shouldRepeat: false };
                                                }}
                                            >
                                                {({ remainingTime }) => {
                                                    const minutes = Math.floor(remainingTime / 60);
                                                    const seconds = remainingTime % 60;
                                                    return (
                                                        <div className="text-center">
                                                            <div className="fw-bold">
                                                                {minutes}:{seconds < 10 ? '0' : ''}{seconds}
                                                            </div>
                                                            <small className="text-muted">Time Left</small>
                                                        </div>
                                                    );
                                                }}
                                            </CountdownCircleTimer>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="test-content">
                        {renderQuestion()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseMockTest;
